/*
 * File: index.tsx
 * Desc: 基础表格组件
 * File Created: 2019-10-11 09:45:00
 * Author: chenghao at <hao.cheng@karakal.com.cn>
 * ------
 * Copyright 2019 - present, karakal
 */
import React, { useEffect, useState, useCallback, useImperativeHandle, useRef } from 'react';
import { Table } from 'antd';
import { TableProps, TableRowSelection } from 'antd/lib/table';
import { PrototypeCheck } from '../utils';
import { PaginationProps } from 'antd/lib/pagination';
import isEqual from 'react-fast-compare';
import { usePrevious } from 'react-use';

interface IPagination {
    total: number;
    pageNo: number;
    pageSize: number;
}

interface IRowSelectoion extends TableRowSelection<any> {
    onChangePro?: (keys: any[], rows: any[]) => void;
}

export interface TableBaseProps extends TableProps<any> {
    columns: any[];
    fetchApi?: Function;
    /** 分页请求的API函数，不传则分页和数据请求用同一个函数 */
    fetchPageApi?: Function;
    apiParams?: any;
    rowClassName?: (record: any, index?: number) => string | string;
    asyncPagination?: boolean;
    /** 分页数据回调-重构分页props */
    paginationCb?: (
        data: TableProps<any>['pagination'],
        defaultConfig: any
    ) => TableProps<any>['pagination'] | false;
    /** 勾选回调，传了onRowSelect显示表格勾选 */
    rowSelection?: IRowSelectoion;
    onPageChange?: (pageNo: number, pageSize: number) => void;
    /** 分页时是否自动请求数据 */
    onPageChangeFetch?: boolean;
    /** 是否禁用初始请求 */
    disableMountFetch?: boolean;
    /** 格式化接口数据，处理各种数据返回 */
    normalizeResult?: (res: any) => any;
    /** 格式化table数据和pagination一起返回的情况 */
    normalizePageResult?: (res: any) => any;
    /** 静态表格数据源（不请求接口时使用） */
    // dataSource?: any[];
    /** 数据发生变化时调用 */
    onDataSourceChange?: (datas: any[]) => void;
    /** 隐藏分页 */
    hidePagination?: boolean;
    compare?: any;
    clearSelectedRowKeys?: boolean;
}

const pageSizeOptions = ['20', '50', '100', '200'];
const TableBase = (props: TableBaseProps, ref: any) => {
    const {
        apiParams,
        fetchApi,
        asyncPagination,
        disableMountFetch,
        normalizeResult,
        normalizePageResult,
        rowSelection,
        fetchPageApi,
        onDataSourceChange,
        onPageChangeFetch,
        onPageChange,
        paginationCb,
        columns,
        className,
        style,
        rowKey,
        rowClassName,
        dataSource: sourceData,
        hidePagination,
        clearSelectedRowKeys = true,
        ...restProps
    } = props;
    const [dataSource, setDataSource] = useState<any[]>(sourceData || []);
    const [paginationData, setPaginationData] = useState<IPagination>();
    const [selectedRowKeys, setSelectRowKeys] = useState<string[] | number[]>(
        rowSelection && rowSelection.selectedRowKeys ? rowSelection.selectedRowKeys : []
    );
    const [loading, setLoading] = useState(false);
    const requestTimes = useRef(0);
    const prevApiParams = usePrevious(apiParams);

    const fetchApiMemoized = useCallback(
        (extraParams?: any, refresh?: boolean) => {
            /** 比较apiParams，避免函数组件和class组件之前，memo不起效果的问题 */
            function compareApiParms() {
                if (PrototypeCheck.isFunction(prevApiParams))
                    return isEqual(apiParams(), prevApiParams());
                return isEqual(apiParams, prevApiParams);
            }

            // 禁止初始化请求
            if (disableMountFetch && !apiParams) {
                return;
            }
            if (!fetchApi) return;
            if (compareApiParms() && !refresh) return;
            setLoading(true);
            // fetchCount.current++;
            requestTimes.current++;
            const temprequestTimes = requestTimes.current;
            /**
             * 最终的参数
             * @description apiparams可以传回调函数用于特殊处理参数
             */
            const finalParams = PrototypeCheck.isFunction(apiParams)
                ? apiParams(extraParams)
                : { ...apiParams, ...extraParams };
            /** 处理分页数据 */
            function handlePagitation(data: any) {
                if (!checkLatestRequest()) return;
                if (hidePagination) return;
                setPaginationData({
                    total: data.total,
                    pageSize: data.pageSize,
                    pageNo: data.pageNo,
                });
            }
            fetchApi(finalParams).then((res: any) => {
                if (!checkLatestRequest()) return;
                setLoading(false);
                // 重置勾选
                // requestTimes.current++;
                // requestTimes.current !== 1 && setSelectRowKeys([]);
                clearSelectedRowKeys && setSelectRowKeys([]);
                setDataSource(normalizeResult ? normalizeResult(res) : res.data.list);
                if (!asyncPagination) {
                    handlePagitation(normalizePageResult ? normalizePageResult(res) : res.data);
                }
            });

            // 异步分页时调用
            if (asyncPagination) {
                (fetchPageApi ? fetchPageApi(finalParams) : fetchApi(finalParams)).then(
                    (res: any) =>
                        handlePagitation(normalizeResult ? normalizeResult(res) : res.data)
                );
            }

            function checkLatestRequest() {
                return temprequestTimes === requestTimes.current;
            }
        },
        [
            fetchApi,
            apiParams,
            prevApiParams,
            asyncPagination,
            disableMountFetch,
            fetchPageApi,
            hidePagination,
            normalizeResult,
            normalizePageResult,
        ]
    );

    useImperativeHandle(
        ref,
        () => ({
            setDataSource,
            setSelectRowKeys,
            selectedRowKeys,
            getDataSource: () => dataSource,
            refresh: () => fetchApiMemoized(null, true),
        }),
        [dataSource, fetchApiMemoized, selectedRowKeys]
    );

    useEffect(() => {
        onDataSourceChange && onDataSourceChange(dataSource);
    }, [dataSource, onDataSourceChange]);

    // 初始时获取基础表格数据
    useEffect(() => {
        fetchApiMemoized();
    }, [fetchApiMemoized]);

    /** 显示分页总量数据 */
    function showTotal() {
        return `共计${Math.ceil(paginationData!.total / paginationData!.pageSize)}页, ${
            paginationData!.total
        }条`;
    }

    function handlePageChange(pageNo: number, pageSize: number) {
        onPageChangeFetch && fetchApiMemoized({ pageNo, pageSize });
        onPageChange && onPageChange(pageNo, pageSize);
    }
    let pagination;
    if (paginationData) {
        let defaultPageConfig = {
            size: 'small',
            total: paginationData.total,
            pageSize: paginationData.pageSize,
            current: paginationData.pageNo,
            pageSizeOptions,
            showTotal: showTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: handlePageChange,
            onShowSizeChange: (_pageNo: number, pageSize: number) => handlePageChange(1, pageSize),
        };
        pagination = paginationCb
            ? paginationCb(paginationData, defaultPageConfig)
            : defaultPageConfig;
    }

    function onRowSelectChange(selectedRowKeys: string[] | number[], selectedRows: any[]) {
        setSelectRowKeys(selectedRowKeys);
        rowSelection &&
            rowSelection.onChangePro &&
            rowSelection.onChangePro(selectedRowKeys, selectedRows);
    }

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            className={className}
            style={style}
            rowKey={rowKey}
            pagination={hidePagination ? false : (pagination as PaginationProps)}
            rowClassName={(record: any, index: number) =>
                (PrototypeCheck.isFunction(rowClassName)
                    ? rowClassName!(record, index)
                    : rowClassName) as string
            }
            {...{
                ...(rowSelection && {
                    rowSelection: { ...rowSelection, onChange: onRowSelectChange, selectedRowKeys },
                }),
                ...restProps,
            }}
        />
    );
};

function compare(prevProps: TableBaseProps, nextProps: TableBaseProps): boolean {
    const keys: (keyof TableBaseProps)[] = ['apiParams', 'compare'];
    return keys.every(key => isEqual(prevProps[key], nextProps[key]));
}

export default React.memo(React.forwardRef(TableBase), compare);
